import type { PerformanceEventProperties } from '@ms/yammer-telemetry';
import type { PageLoadPerformanceEventProperties, PageName } from '@ms/yammer-telemetry-support';
import type { Emitter } from 'mitt';
import mitt from 'mitt';

export type PageLoadEventName =
  | 'Loaded'
  | 'TimedOut'
  | 'Failed'
  | 'FailedRecoverable'
  | 'FailedExpected'
  | 'Interrupted';
export type RouteChangedEventName = 'RouteChanged';
export type RouteCommittedEventName = 'RouteCommitted';

export type PageLoadEventData = PerformanceEventProperties<PageLoadPerformanceEventProperties> & {
  /**
   * Correlates an outcome event (`Loaded`/`Failed`/`FailedRecoverable`/`FailedExpected`/`Interrupted`/`TimedOut`)
   * back to the `RouteCommitted` that started it, carrying the same `pageLoadId`. A subscriber can
   * require an exact id match so a stale outcome from a previous navigation to the same `page` does
   * not settle or cancel the current one. Optional because only page-route-telemetry's own
   * reporting path populates it; other emitters may omit it.
   */
  readonly pageLoadId?: number;
};

/**
 * Fired once per route as its telemetry state commits (initial load and every client-side
 * navigation), before the page paints. Carries the stable route identity so a subscriber can
 * correlate this navigation start with the outcome-bearing `Loaded`/`Failed` event that follows.
 */
export interface RouteCommittedEventData {
  readonly page: PageName;
  readonly pathname: string;
  readonly pageLoadId: number;
}

type LoadEvents = Record<PageLoadEventName, PageLoadEventData>;
type RouteChangedEvents = Record<RouteChangedEventName, void>;
type RouteCommittedEvents = Record<RouteCommittedEventName, RouteCommittedEventData>;
type PageLoadEvents = LoadEvents & RouteChangedEvents & RouteCommittedEvents;

let emitter: Emitter<PageLoadEvents>;

type SubscribeToPageLoadEvent = <E extends keyof PageLoadEvents>(
  event: E,
  callback: (data: PageLoadEvents[E]) => void
) => void;
export const subscribeToPageLoadEvent: SubscribeToPageLoadEvent = (event, callback) => {
  if (!emitter) {
    emitter = mitt<PageLoadEvents>();
  }
  emitter.on(event, callback);
};

type UnsubscribeFromPageLoadEvent = <E extends keyof PageLoadEvents>(
  event: E,
  callback: (data: PageLoadEvents[E]) => void
) => void;
export const unsubscribeFromPageLoadEvent: UnsubscribeFromPageLoadEvent = (event, callback) => {
  if (!emitter) {
    return;
  }

  emitter.off(event, callback);
};

type EmitPageLoadEventArgs<E extends keyof PageLoadEvents> = E extends RouteChangedEventName
  ? []
  : [data: PageLoadEvents[E]];

/**
 * @internal
 * Emits a page load lifecycle event. This should be considered a private API and should
 * not be called outside of the page-route-telemetry package except for test scenarios.
 */
export const emitPageLoadEvent = <E extends keyof PageLoadEvents>(
  event: E,
  ...args: EmitPageLoadEventArgs<E>
): void => {
  if (!emitter) {
    return;
  }

  emitter.emit(event, args[0] as PageLoadEvents[E]);
};
